import React, { useState, useRef, useEffect } from "react";

interface StatusDropdownProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (newValue: string) => void;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  value,
  options,
  onChange,
}) => {
  const getStatusStyles = (status: string) => {
    const baseStyles = {
      borderRadius: "24px",
      fontSize: 14,
      padding: "5px 10px",
      border: "1px solid #ddd",
      textAlign: "center" as const,
      // display: "inline-block",
      minWidth: "100px",
      cursor: "pointer",
    };

    switch (status?.toUpperCase()) {
      case "OPEN":
        return {
          ...baseStyles,
          background: "#FFF1E3",
          color: "#913035",
        };
      case "IN_PROGRESS":
      case "PROGRESS":
        return {
          ...baseStyles,
          background: "#FFFCC2",
          color: "#9F6820",
        };
      case "RESOLVED":
        return {
          ...baseStyles,
          background: "#EAFFE3",
          color: "#30916B",
        };
      default:
        return {
          ...baseStyles,
          background: "#f8f9fa",
          color: "#000000",
        };
    }
  };
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    setIsOpen(false);
  };

  const currentOption =
    options.find((opt) => opt.value === value) || options[0];
  const triggerStyles = getStatusStyles(value);

  return (
    <div
      ref={dropdownRef}
      style={{ position: "relative", display: "inline-block", zIndex: 999 }}
    >
      {/* Dropdown Trigger */}
      <div
        style={triggerStyles}
        onClick={(e) => {
          e.stopPropagation(); // ← Add this line to solve clickable-div-inside-another-clickable-div
          setIsOpen(!isOpen);
        }}
        onMouseEnter={(e) => {
          // Add hover effect
          e.currentTarget.style.opacity = "0.9";
          e.currentTarget.style.transform = "scale(1.02)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "1";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {currentOption.label}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1000,
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            minWidth: "120px",
            marginTop: "5px",
          }}
        >
          {options.map((option) => {
            const optionStyles = getStatusStyles(option.value);
            return (
              <div
                key={option.value}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(option.value);
                }}
                style={{
                  ...optionStyles,
                  border: "none",
                  borderRadius: "0",
                  minWidth: "auto",
                  cursor: "pointer",
                  backgroundColor:
                    option.value === value ? optionStyles.background : "white",
                  color:
                    option.value === value ? optionStyles.color : "#000000",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    optionStyles.background;
                  e.currentTarget.style.color = optionStyles.color;
                }}
                onMouseLeave={(e) => {
                  if (option.value === value) {
                    e.currentTarget.style.backgroundColor =
                      optionStyles.background;
                    e.currentTarget.style.color = optionStyles.color;
                  } else {
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.color = "#000000";
                  }
                }}
              >
                {option.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatusDropdown;
